import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

interface DateRangeSelectorProps {
    fullWidth?: boolean;
    label?: string;
    defaultDate?: Dayjs | null;
    onDateChange?: (date: Dayjs) => void;
    error?: {
        message?: string;
    };
    required?: boolean;
    inputFormat?: string;
    defaultToNull?: boolean;
    onClearValue?: () => void;
}

const DateRangeSelector = (props: DateRangeSelectorProps) => {
    const {
        label,
        onDateChange,
        required,
        inputFormat,
        fullWidth,
        error,
        defaultDate,
        defaultToNull,
        onClearValue
    } = props;

    const [value, setValue] = useState<Dayjs | null>(
        defaultDate ?? (defaultToNull ? null : dayjs())
    );

    const [validDateError, setValidDateError] = useState<string>('');

    // keep state in sync if defaultDate changes
    useEffect(() => {
        if (defaultDate !== undefined) {
            setValue(defaultDate);
        }
    }, [defaultDate]);

    const handleChange = (newValue: Dayjs | null) => {
        // allow clearing
        if (!newValue) {
            setValue(null);
            setValidDateError('');
            return;
        }

        const isValidDate =
            dayjs(newValue).isValid() &&
            dayjs(newValue).isAfter(dayjs(new Date(1900, 0, 1))) &&
            dayjs(newValue).isBefore(dayjs().add(10, 'year'));

        if (!isValidDate) {
            setValidDateError('Invalid date');
            return;
        }

        setValidDateError('');
        setValue(newValue);
        onDateChange?.(newValue);
    };

    const handleClearValue = () => {
        setValue(null);
        setValidDateError('');
        if (onClearValue) {
            onClearValue();
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                label={label || 'Date Picker'}
                format={inputFormat || 'DD/MM/YYYY'}
                value={value}
                onChange={handleChange}
                slotProps={{
                    field: {
                        clearable: true,
                        onClear: handleClearValue
                    },
                    textField: {
                        helperText: error?.message || validDateError,
                        error: Boolean(error?.message || validDateError),
                        required,
                        fullWidth
                    }
                }}
            />
        </LocalizationProvider>
    );
};

export default DateRangeSelector;
